import { API_BASE_URL } from '../Utils/constants';

/**
 * Fallback solo Córdoba Capital (Ordenanza 8256 / IDECOR).
 * Cuando el backend envíe URLs en la respuesta, estas constantes dejan de usarse.
 */
export const CORDOBA_URBAN_ZONING_DOCS_BASE_URL =
  'https://obs-idecor-mapas-docs.obs.sa-argentina-1.myhuaweicloud.com/muni_cba/ord_8256/';

export const CORDOBA_RETIRO_LINEA_EDIFICACION_URL =
  `${CORDOBA_URBAN_ZONING_DOCS_BASE_URL}plano_de_retiros_de_LE.pdf`;

/**
 * Contrato recomendado en GET /geturbanzoning (futuro):
 * - `data.documents_base_url` — base común de PDFs de la jurisdicción.
 * - `data.properties.retiro_le` — URL absoluta o nombre de archivo.
 * - `data.properties.enlace_grafico` — URL absoluta o nombre de archivo.
 * - Opcional: `data.properties.retiro_le_url` / `enlace_grafico_url` si se quiere
 *   separar etiqueta de archivo y link.
 */
const isAbsoluteUrl = (value) => /^https?:\/\//i.test(String(value || '').trim());

const trimValue = (value) => String(value ?? '').trim();

const normalizeDocFilename = (filename) => {
  const raw = trimValue(filename);
  if (!raw || isAbsoluteUrl(raw)) return raw;

  const aliases = {
    'plano_de_retiros_de_l.e..pdf': 'plano_de_retiros_de_LE.pdf',
    'plano_de_retiros_de_le.pdf': 'plano_de_retiros_de_LE.pdf',
  };

  const lowered = raw.toLowerCase();
  if (aliases[lowered]) return aliases[lowered];

  return raw.replace(/\.+(?=pdf$)/i, '.');
};

const joinBaseAndFilename = (baseUrl, filename) => {
  const base = trimValue(baseUrl).replace(/\/+$/, '');
  const file = normalizeDocFilename(filename);
  if (!base || !file || isAbsoluteUrl(file)) return file || null;
  return `${base}/${encodeURIComponent(file)}`;
};

/**
 * Resuelve la URL de un documento de zonificación (Córdoba Capital).
 * Prioridad: URL explícita del backend → URL en el campo → base + archivo → fallback local.
 */
export const resolveUrbanZoningDocumentUrl = (
  fieldId,
  rawValue,
  { properties = {}, documentsBaseUrl = null } = {},
) => {
  const props = properties && typeof properties === 'object' ? properties : {};
  const explicitUrl = trimValue(props[`${fieldId}_url`]);
  if (explicitUrl && isAbsoluteUrl(explicitUrl)) return explicitUrl;

  const value = trimValue(rawValue);
  if (value && isAbsoluteUrl(value)) return value;

  const baseFromApi = trimValue(documentsBaseUrl) || trimValue(props.documents_base_url);
  if (value && baseFromApi) {
    return joinBaseAndFilename(baseFromApi, value);
  }

  // Fallback temporal mientras el backend no persiste URLs completas.
  if (fieldId === 'retiro_le') {
    return CORDOBA_RETIRO_LINEA_EDIFICACION_URL;
  }

  if (value) {
    return joinBaseAndFilename(CORDOBA_URBAN_ZONING_DOCS_BASE_URL, value);
  }

  return null;
};

/**
 * Obtiene el detalle de normas de zonificación urbana (Córdoba Capital).
 * No requiere autenticación.
 *
 * @param {string} zoningPerfil — valor de `data.zoning_perfil` del detalle del lote
 * @returns {Promise<{ ok: boolean, data: object|null, status: number, message?: string }>}
 */
export const getUrbanZoning = async (zoningPerfil) => {
  const perfil = String(zoningPerfil || '').trim();
  if (!perfil) {
    return { ok: false, data: null, status: 400, message: 'zoning_perfil requerido' };
  }

  const params = new URLSearchParams({ zoning_perfil: perfil });
  const response = await fetch(`${API_BASE_URL}/geturbanzoning?${params}`, {
    method: 'GET',
    headers: { Accept: 'application/json' },
  });

  let json = null;
  try {
    json = await response.json();
  } catch {
    json = {};
  }

  if (!response.ok) {
    return {
      ok: false,
      data: null,
      status: response.status,
      message: json?.message || '',
    };
  }

  return {
    ok: true,
    data: json?.data ?? null,
    status: response.status,
    message: json?.message || '',
  };
};
